import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, ChevronUp } from "lucide-react";
import RangeSliderInput from "@/components/forms/inputs/RangeSliderInput";

type RangeFilterProps = {
  label: string;
  min: number;
  max: number;
  valueMin?: number;
  valueMax?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  formatValue?: (val: number) => string;
  onChange: (range: { min: number; max: number }) => void;
};

export const RangeFilter: React.FC<RangeFilterProps> = ({
  label,
  min,
  max,
  valueMin,
  valueMax,
  step,
  disabled,
  className,
  formatValue,
  onChange
}) => {
  const computedStep = useMemo(() => {
    if (typeof step === "number" && step > 0) return step;
    const span = Math.max(1, max - min);
    // choose ~20 steps for smooth control
    return Math.max(1, Math.round(span / 20));
  }, [min, max, step]);

  const [open, setOpen] = useState(false);

  const formattedMin =
    typeof valueMin === "number"
      ? formatValue
        ? formatValue(valueMin)
        : String(valueMin)
      : formatValue
        ? formatValue(min)
        : String(min);
  const formattedMax =
    typeof valueMax === "number"
      ? formatValue
        ? formatValue(valueMax)
        : String(valueMax)
      : formatValue
        ? formatValue(max)
        : String(max);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          `subtext-small flex h-[40px] w-fit cursor-pointer items-center justify-between overflow-hidden rounded border border-gray-dark-200
            bg-gray-dark-600 px-3 py-2 text-left text-gray-dark-2200 focus:outline-0 disabled:cursor-not-allowed disabled:opacity-50
            lg:min-w-[295px] lg:max-w-[350px] ${className}`
        )}
        disabled={disabled}
        asChild={false}
      >
        <div className="flex h-[40px] w-full items-center justify-between">
          <div className="flex flex-1 flex-row items-center justify-between">
            <span className="mr-2 truncate">{label ? `${label}: ` : ""}</span>
            <span className="truncate">
              {formattedMin} - {formattedMax}
            </span>
          </div>
          <div className="ml-3 flex h-[40px] min-w-6 items-center justify-center border-l-[1px] bg-gray-dark-600 pl-[10px]">
            {open ? (
              <ChevronUp className="color-gray-dark-2200 h-4 w-4" />
            ) : (
              <ChevronDown className="color-gray-dark-2200 h-4 w-4" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-4" align="start">
        <div className="flex w-full flex-col gap-2">
          {label ? <div className="label-x-small text-gray-dark-1500">{label}</div> : null}
          <div className="flex flex-col gap-3">
            <RangeSliderInput
              min={min}
              max={max}
              step={computedStep}
              valueMin={valueMin ?? min}
              valueMax={valueMax ?? max}
              disabled={disabled}
              formatLabel={formatValue}
              onChange={onChange}
              className="h-[32px]"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
